import { Configuration } from "./generated";
import axios, { type AxiosInstance } from "axios";
import type { ApiResult, AuthConfig } from "../types/api";

// ─── Backends connus ──────────────────────────────────────────────────────────

let selectedBackend = 0;
export const KNOWN_BACKENDS: string[] = [
    "http://localhost:8080",
    "http://dev.promise.lange.xyz",
    "https://data.promise.lange.xyz",
    "https://rea.data.promise.lange.xyz",
    "https://limoges.data.promise.lange.xyz",
    "https://jamrai2.data.promise.lange.xyz/",
];

export const MAIN_SERVER_URL: string = KNOWN_BACKENDS[selectedBackend];

function resolveServerUrl(): string {
    const { protocol, hostname, host } = window.location;
    if (hostname === "localhost") {
        return  MAIN_SERVER_URL;
    }
    return protocol + "//" + host;
}

export const serverUrl: string = resolveServerUrl();

if (process.env.NODE_ENV !== "production") {
    console.log("[Utils] Server URL:", serverUrl);
}

// ─── Instances Axios / OpenAPI ────────────────────────────────────────────────

export const config = new Configuration({});
export const axiosInstance: AxiosInstance = axios.create({});

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Retourne la configuration d'en-tête Authorization depuis le sessionStorage. */
export function getConfig(): AuthConfig {
    const token = sessionStorage.getItem("user") ?? localStorage.getItem("user");
    if (token) {
        try {
            const parsed = JSON.parse(token) as { access_token?: string };
            if (parsed.access_token) {
                return {
                    headers: {
                        Authorization: "Bearer " + parsed.access_token,
                    },
                };
            }
        } catch {
            // token malformé — retomber sur headers vides
        }
    }
    return { headers: {} };
}

/**
 * Enrichit la config d'auth avec l'en-tête {@code Accept-Language} pour piloter
 * la langue côté manager-translator (résolution d'alias + labels localisés).
 * Si {@code lang} est null/empty, retourne la config standard.
 */
export function getConfigWithLang(lang?: string | null): AuthConfig {
    const base = getConfig();
    if (!lang) return base;
    return {
        headers: {
            ...base.headers,
            "Accept-Language": lang,
        },
    } as AuthConfig;
}

/**
 * Langue applicative courante. Le frontend peut la piloter depuis l'i18n
 * (voir `i18n.js`). Fallback {@code "en"} si non renseignée.
 */
export function currentLang(): string {
    try {
        const stored = localStorage.getItem("i18nextLng");
        if (stored && stored.length >= 2) return stored.slice(0, 2).toLowerCase();
    } catch { /* noop */ }
    return "en";
}

/** Découpe un tableau en sous-tableaux de taille `size_of_chunk`. */
export function arrayIntoChunks<T>(array: T[], size_of_chunk: number): T[][] {
    const arr: T[][] = [];
    for (let i = 0; i < array.length; i += size_of_chunk) {
        arr.push(array.slice(i, i + size_of_chunk));
    }
    return arr;
}

/**
 * Utilitaire central pour les appels API.
 * Retourne toujours `{ data, error }` — ne lève jamais d'exception.
 *
 * @example
 *   const { data, error } = await apiCall(api.someMethod(args));
 *   if (error) { ... }
 */
export async function apiCall<T>(
    promise: Promise<{ data: T } | T>
): Promise<ApiResult<T>> {
    try {
        const result = await promise;
        const data =
            result !== null &&
            typeof result === "object" &&
            "data" in (result as object)
                ? (result as { data: T }).data
                : (result as T);
        return { data: data ?? null, error: null };
    } catch (err) {
        if (process.env.NODE_ENV !== "production") {
            console.error("[apiCall]", err);
        }
        return { data: null, error: err };
    }
}
